import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/profile-components/Navbar';
import Topbar from '../components/profile-components/Topbar';
import GoalModal from '../components/Dashboard/GoalModal';
import TaskModal from '../components/Tasks/TaskModal';
import API_BASE_URL from '../config';
import usePageTitle from '../hooks/usePageTitle';
import './SalesPages.css';
import './Dashboard.css';

const DASHBOARD_SETTINGS_KEY = 'agentBookDashboardSettings';

const DEFAULT_DASHBOARD_SETTINGS = {
  showPerformance: true,
  showScoreboard: true,
  showAttention: true,
  showPolicyCharts: true,
  showWorkQueue: true,
  showRecentLeads: true,
  showRecentOpportunities: true,
};

const parseMysqlDate = (dateStr) => {
  if (!dateStr) return null;

  const [datePart] = String(dateStr).split('T');
  const [year, month, day] = datePart.split('-').map(Number);

  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const formatDateOnly = (dateStr) => {
  const date = parseMysqlDate(dateStr);
  if (!date) return '—';

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  });
};

const formatTimeOnly = (timeStr) => {
  if (!timeStr || timeStr === '00:00:00') return '';

  const [hh, mm] = String(timeStr).split(':');
  const hours = Number(hh);
  const minutes = Number(mm);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return '';

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
};

function DashboardLineChart({ title, subtitle, data = [] }) {
  const width = 520;
  const height = 210;
  const padding = 30;

  const normalized = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];

    const counts = data.map((item) => Number(item.count || 0));
    const max = Math.max(...counts, 1);
    const min = Math.min(...counts, 0);
    const range = Math.max(max - min, 1);

    return data.map((item, index) => {
      const x =
        padding +
        (index * (width - padding * 2)) / Math.max(data.length - 1, 1);

      const y =
        height -
        padding -
        ((Number(item.count || 0) - min) / range) * (height - padding * 2);

      return {
        ...item,
        count: Number(item.count || 0),
        x,
        y,
      };
    });
  }, [data]);

  const pathD = useMemo(() => {
    if (!normalized.length) return '';

    return normalized
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ');
  }, [normalized]);

  return (
    <div className="sales-table-card dashboard-modern-card dashboard-chart-card">
      <div className="dashboard-card-header compact">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
      </div>

      <div className="dashboard-chart-body">
        {!normalized.length ? (
          <div className="dashboard-chart-empty">No chart data available.</div>
        ) : (
          <svg
            viewBox={`0 0 ${width} ${height}`}
            className="dashboard-line-chart"
            role="img"
            aria-label={title}
          >
            <line
              x1={padding}
              y1={height - padding}
              x2={width - padding}
              y2={height - padding}
              className="dashboard-chart-axis"
            />
            <line
              x1={padding}
              y1={padding}
              x2={padding}
              y2={height - padding}
              className="dashboard-chart-axis"
            />

            <path d={pathD} className="dashboard-chart-line" />

            {normalized.map((point) => (
              <g key={`${point.label}-${point.count}`}>
                <circle cx={point.x} cy={point.y} r="4" className="dashboard-chart-dot" />
                <text
                  x={point.x}
                  y={point.y - 10}
                  textAnchor="middle"
                  className="dashboard-chart-value"
                >
                  {point.count}
                </text>
                <text
                  x={point.x}
                  y={height - 8}
                  textAnchor="middle"
                  className="dashboard-chart-label"
                >
                  {point.label}
                </text>
              </g>
            ))}
          </svg>
        )}
      </div>
    </div>
  );
}

function DashboardDonutChart({ title, subtitle, data = [] }) {
  const total = data.reduce((sum, item) => sum + Number(item.count || 0), 0);
  const radius = 68;
  const strokeWidth = 22;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;

  const slices = data.map((item, index) => {
    const value = Number(item.count || 0);
    const percent = total > 0 ? value / total : 0;
    const dash = percent * circumference;
    const slice = {
      ...item,
      value,
      percent,
      dash,
      offset,
      className: `dashboard-donut-slice dashboard-donut-slice-${(index % 6) + 1}`,
    };
    offset += dash;
    return slice;
  });

  return (
    <div className="sales-table-card dashboard-modern-card dashboard-chart-card">
      <div className="dashboard-card-header compact">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
      </div>

      <div className="dashboard-donut-layout">
        <div className="dashboard-donut-wrap">
          {total <= 0 ? (
            <div className="dashboard-chart-empty">No active policies found.</div>
          ) : (
            <svg viewBox="0 0 180 180" className="dashboard-donut-chart" role="img" aria-label={title}>
              <circle
                cx="90"
                cy="90"
                r={radius}
                fill="none"
                stroke="#e2e8f0"
                strokeWidth={strokeWidth}
              />

              {slices.map((slice) => (
                <circle
                  key={slice.label}
                  cx="90"
                  cy="90"
                  r={radius}
                  fill="none"
                  strokeWidth={strokeWidth}
                  strokeDasharray={`${slice.dash} ${circumference - slice.dash}`}
                  strokeDashoffset={-slice.offset}
                  strokeLinecap="butt"
                  className={slice.className}
                  transform="rotate(-90 90 90)"
                />
              ))}

              <text x="90" y="84" textAnchor="middle" className="dashboard-donut-total">
                {total}
              </text>
              <text x="90" y="104" textAnchor="middle" className="dashboard-donut-label">
                Active
              </text>
            </svg>
          )}
        </div>

        <div className="dashboard-donut-legend">
          {slices.length === 0 ? (
            <div className="dashboard-empty-row">No coverage data</div>
          ) : (
            slices.map((slice, index) => (
              <div key={slice.label} className="dashboard-donut-legend-row">
                <span className={`dashboard-donut-dot dashboard-donut-dot-${(index % 6) + 1}`} />
                <span>{slice.label}</span>
                <strong>{slice.value}</strong>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  usePageTitle('Dashboard');

  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [goals, setGoals] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);

  const [tasks, setTasks] = useState([]);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentOpps, setRecentOpps] = useState([]);

  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);

  const [activePerformanceTab, setActivePerformanceTab] = useState('opportunities');
  const [activeWorkTab, setActiveWorkTab] = useState('upcoming');
  const [showDashboardSettings, setShowDashboardSettings] = useState(false);

  const [dashboardSettings, setDashboardSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(DASHBOARD_SETTINGS_KEY);

      return saved
        ? { ...DEFAULT_DASHBOARD_SETTINGS, ...JSON.parse(saved) }
        : DEFAULT_DASHBOARD_SETTINGS;
    } catch {
      return DEFAULT_DASHBOARD_SETTINGS;
    }
  });

  const metricMeta = [
    { key: 'opportunities', title: 'Opportunities' },
    { key: 'submittedApps', title: 'Submitted Applications' },
    { key: 'newPolicies', title: 'New Policies' },
    { key: 'clients', title: 'New Clients' },
  ];

  const metricTitleMap = Object.fromEntries(metricMeta.map((m) => [m.key, m.title]));

  useEffect(() => {
    localStorage.setItem(DASHBOARD_SETTINGS_KEY, JSON.stringify(dashboardSettings));
  }, [dashboardSettings]);

  const refreshSummary = async () => {
    try {
      setLoadingSummary(true);

      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [summaryRes, goalsRes, chartsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/dashboard/summary`, { headers }),
        fetch(`${API_BASE_URL}/api/goals`, { headers }),
        fetch(`${API_BASE_URL}/api/dashboard/charts`, { headers }),
      ]);

      const summaryData = summaryRes.ok ? await summaryRes.json() : null;
      const goalsData = goalsRes.ok ? await goalsRes.json() : [];
      const chartsData = chartsRes.ok ? await chartsRes.json() : null;

      setSummary(summaryData && typeof summaryData === 'object' ? summaryData : null);
      setChartData(chartsData && typeof chartsData === 'object' ? chartsData : null);

      const goalMap = {};

      if (Array.isArray(goalsData)) {
        goalsData.forEach((goal) => {
          goalMap[goal.category] = goal.goal;
        });
      }

      setGoals(goalMap);
    } catch (err) {
      console.error('❌ Dashboard summary load failed:', err);
      setSummary(null);
      setChartData(null);
    } finally {
      setLoadingSummary(false);
    }
  };

  const refreshTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [tasksRes, overdueRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/tasks/upcoming`, { headers }),
        fetch(`${API_BASE_URL}/api/tasks/overdue`, { headers }),
      ]);

      const tasksData = tasksRes.ok ? await tasksRes.json() : [];
      const overdueData = overdueRes.ok ? await overdueRes.json() : [];

      setTasks(Array.isArray(tasksData) ? tasksData : []);
      setOverdueTasks(Array.isArray(overdueData) ? overdueData : []);
    } catch (err) {
      console.error('❌ Dashboard task load failed:', err);
      setTasks([]);
      setOverdueTasks([]);
    }
  };

  const refreshRecentActivity = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [leadsRes, oppsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/leads/recent`, { headers }),
        fetch(`${API_BASE_URL}/api/opportunities/recent`, { headers }),
      ]);

      const leadsData = leadsRes.ok ? await leadsRes.json() : [];
      const oppsData = oppsRes.ok ? await oppsRes.json() : [];

      setRecentLeads(Array.isArray(leadsData) ? leadsData : []);
      setRecentOpps(Array.isArray(oppsData) ? oppsData : []);
    } catch (err) {
      console.error('❌ Dashboard recent activity load failed:', err);
      setRecentLeads([]);
      setRecentOpps([]);
    }
  };

  useEffect(() => {
    refreshSummary();
    refreshTasks();
    refreshRecentActivity();
  }, []);

  const getMetric = (key) => {
    const metric = summary?.metrics?.[key];

    return {
      actual: Number(metric?.actual || 0),
      goal: Number(metric?.goal ?? goals[key] ?? 0),
      percent: Number(metric?.percent || 0),
      paceStatus: metric?.paceStatus || 'no_goal',
    };
  };

  const paceLabelMap = {
    goal_hit: 'Goal Hit',
    ahead: 'Ahead',
    on_pace: 'On Pace',
    behind: 'Behind',
    no_goal: 'No Goal',
  };

  const paceClassMap = {
    goal_hit: 'sales-chip qualified',
    ahead: 'sales-chip qualified',
    on_pace: 'sales-chip contacted',
    behind: 'sales-chip past_due',
    no_goal: 'sales-chip sales-chip-neutral',
  };

  const scoreboard = useMemo(() => {
    const metrics = metricMeta.map(({ key, title }) => {
      const metric = getMetric(key);
      const remaining = Math.max(metric.goal - metric.actual, 0);

      return {
        key,
        title,
        actual: metric.actual,
        goal: metric.goal,
        remaining,
        isHit: metric.goal > 0 && metric.actual >= metric.goal,
        paceStatus: metric.paceStatus,
      };
    });

    const goalsWithTargets = metrics.filter((metric) => metric.goal > 0);
    const goalsHit = goalsWithTargets.filter((metric) => metric.isHit).length;

    const closestGoal =
      goalsWithTargets
        .filter((metric) => !metric.isHit)
        .sort((a, b) => a.remaining - b.remaining)[0] || null;

    const biggestGap =
      goalsWithTargets
        .filter((metric) => !metric.isHit)
        .sort((a, b) => b.remaining - a.remaining)[0] || null;

    const daysElapsed = Number(summary?.daysElapsed || 0);
    const daysInMonth = Number(summary?.daysInMonth || 0);
    const daysLeft = Math.max(daysInMonth - daysElapsed, 0);

    return {
      goalsHit,
      totalGoals: goalsWithTargets.length,
      closestGoal,
      biggestGap,
      daysLeft,
    };
  }, [summary, goals]);

  const activeMetric = getMetric(activePerformanceTab);

  const attentionItems = [
    { label: 'Overdue Tasks', value: summary?.attention?.overdueTasks ?? 0 },
    { label: 'Tasks Due Today', value: summary?.attention?.dueToday ?? 0 },
    { label: 'Overdue Opportunities', value: summary?.attention?.oppOverdue ?? 0 },
    { label: 'Missing Next Step', value: summary?.attention?.missingNextStep ?? 0 },
  ];

  const formatDue = (dateStr, timeStr) => {
    const date = parseMysqlDate(dateStr);
    if (!date) return '—';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const taskDay = new Date(date);
    taskDay.setHours(0, 0, 0, 0);

    const diff = (taskDay - today) / (1000 * 60 * 60 * 24);
    const timeFormatted = formatTimeOnly(timeStr);

    if (diff === 0) return `Today${timeFormatted ? ` ${timeFormatted}` : ''}`;
    if (diff === 1) return `Tomorrow${timeFormatted ? ` ${timeFormatted}` : ''}`;

    return `${formatDateOnly(dateStr)}${timeFormatted ? ` ${timeFormatted}` : ''}`;
  };

  const getTaskTitle = (task) => {
    return [task.full_name || 'Unknown', task.type || 'Task', task.title || 'Untitled']
      .filter(Boolean)
      .join(' – ');
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const toggleDashboardSetting = (key) => {
    setDashboardSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const resetDashboardSettings = () => {
    setDashboardSettings(DEFAULT_DASHBOARD_SETTINGS);
  };

  const renderPerformanceWidget = () => {
    if (!dashboardSettings.showPerformance) return null;

    return (
      <div className="sales-table-card dashboard-modern-card dashboard-performance-card">
        <div className="dashboard-card-header compact">
          <div>
            <h2>Performance</h2>
            <p>{summary?.monthLabel ? `${summary.monthLabel} production` : 'Monthly production'}</p>
          </div>

          <button
            type="button"
            className="sales-secondary-btn"
            onClick={() => setEditingKey(activePerformanceTab)}
          >
            Edit Goal
          </button>
        </div>

        <div className="dashboard-tabs compact-tabs">
          {metricMeta.map((item) => (
            <button
              key={item.key}
              type="button"
              className={activePerformanceTab === item.key ? 'active' : ''}
              onClick={() => setActivePerformanceTab(item.key)}
            >
              {item.title}
            </button>
          ))}
        </div>

        <div className="dashboard-performance-body">
          <div>
            <div className="dashboard-kpi-value compact-value">
              {loadingSummary ? '—' : `${activeMetric.actual} / ${activeMetric.goal || 0}`}
            </div>

            <div className="dashboard-kpi-progress compact-progress">
              <div
                className="dashboard-kpi-progress-bar"
                style={{ width: `${Math.min(activeMetric.percent, 100)}%` }}
              />
            </div>
          </div>

          <div className="dashboard-performance-meta">
            <span>{activeMetric.percent}% complete</span>
            <span className={paceClassMap[activeMetric.paceStatus]}>
              {paceLabelMap[activeMetric.paceStatus]}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderScoreboardWidget = () => {
    if (!dashboardSettings.showScoreboard) return null;

    return (
      <div className="sales-table-card dashboard-modern-card">
        <div className="dashboard-card-header compact">
          <div>
            <h2>Scoreboard</h2>
            <p>Month at a glance</p>
          </div>
        </div>

        <div className="dashboard-compact-list">
          <div className="dashboard-funnel-row">
            <span>Goals Hit</span>
            <strong>{scoreboard.goalsHit} / {scoreboard.totalGoals}</strong>
          </div>

          <div className="dashboard-funnel-row">
            <span>Closest Goal</span>
            <strong>
              {scoreboard.closestGoal
                ? `${scoreboard.closestGoal.title} ${scoreboard.closestGoal.actual}/${scoreboard.closestGoal.goal}`
                : '—'}
            </strong>
          </div>

          <div className="dashboard-funnel-row">
            <span>Biggest Gap</span>
            <strong>
              {scoreboard.biggestGap
                ? `${scoreboard.biggestGap.title} ${scoreboard.biggestGap.actual}/${scoreboard.biggestGap.goal}`
                : '—'}
            </strong>
          </div>

          <div className="dashboard-funnel-row">
            <span>Days Left</span>
            <strong>{scoreboard.daysLeft}</strong>
          </div>
        </div>
      </div>
    );
  };

  const renderAttentionWidget = () => {
    if (!dashboardSettings.showAttention) return null;

    return (
      <div className="sales-table-card dashboard-modern-card">
        <div className="dashboard-card-header compact">
          <div>
            <h2>Attention Needed</h2>
            <p>Items that need action</p>
          </div>
        </div>

        <div className="dashboard-mini-grid attention-grid">
          {attentionItems.map((item) => (
            <div key={item.label} className="dashboard-mini-stat">
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPolicyCharts = () => {
    if (!dashboardSettings.showPolicyCharts) return null;

    return (
      <div className="dashboard-policy-chart-section">
        <div className="dashboard-section-heading">
          <div>
            <h2>Policy & Application Trends</h2>
            <p>Track application momentum, policy growth, and active policy mix.</p>
          </div>
        </div>

        <div className="dashboard-chart-grid">
          <DashboardLineChart
            title="Submitted Applications"
            subtitle="Last 6 months"
            data={chartData?.submittedApplicationsTrend || []}
          />

          <DashboardLineChart
            title="Total Policies"
            subtitle="Cumulative policies over time"
            data={chartData?.totalPoliciesTrend || []}
          />

          <DashboardDonutChart
            title="Active Policies by Coverage Type"
            subtitle="Current active book of business"
            data={chartData?.activePoliciesByCoverageType || []}
          />
        </div>
      </div>
    );
  };

  const renderTaskTable = (items, emptyText, overdue = false) => (
    <div className="dashboard-simple-table-wrap">
      <table className="dashboard-simple-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Task</th>
          </tr>
        </thead>

        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan="2" className="dashboard-empty-row">
                {emptyText}
              </td>
            </tr>
          ) : (
            items.map((task) => (
              <tr key={task.id} onClick={() => handleTaskClick(task)}>
                <td className={overdue ? 'dashboard-danger-text' : ''}>
                  {formatDue(task.due_date, task.start_time)}
                </td>
                <td>{getTaskTitle(task)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const renderRecentLeadsTable = () => (
    <div className="dashboard-simple-table-wrap">
      <table className="dashboard-simple-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Phone</th>
          </tr>
        </thead>

        <tbody>
          {recentLeads.length === 0 ? (
            <tr>
              <td colSpan="2" className="dashboard-empty-row">
                No recent leads
              </td>
            </tr>
          ) : (
            recentLeads.map((lead) => (
              <tr key={lead.id} onClick={() => navigate(`/lead-profile/${lead.id}`)}>
                <td>
                  {lead.first_name && lead.last_name
                    ? `${lead.first_name} ${lead.last_name}`
                    : 'Unnamed Lead'}
                </td>
                <td>{lead.phone || '—'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const renderRecentOpportunitiesTable = () => (
    <div className="dashboard-simple-table-wrap">
      <table className="dashboard-simple-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Coverage</th>
            <th>Status</th>
          </tr>
        </thead>

        <tbody>
          {recentOpps.length === 0 ? (
            <tr>
              <td colSpan="3" className="dashboard-empty-row">
                No recent opportunities
              </td>
            </tr>
          ) : (
            recentOpps.map((opp) => (
              <tr
                key={opp.id}
                onClick={() => {
                  const path = opp.client_id ? 'client' : 'lead';
                  navigate(`/${path}-profile/${opp.client_id || opp.lead_id}?opportunityId=${opp.id}`);
                }}
              >
                <td>{opp.name || 'Unnamed Opportunity'}</td>
                <td>{opp.coverageTypeName || '—'}</td>
                <td>{opp.status || '—'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const renderWorkQueueWidget = () => {
    if (!dashboardSettings.showWorkQueue) return null;

    const tabs = [
      { key: 'upcoming', label: 'Upcoming Tasks' },
      { key: 'overdue', label: 'Overdue Tasks' },
      ...(dashboardSettings.showRecentLeads ? [{ key: 'leads', label: 'Recent Leads' }] : []),
      ...(dashboardSettings.showRecentOpportunities
        ? [{ key: 'opportunities', label: 'Recent Opportunities' }]
        : []),
    ];

    const activeTabStillVisible = tabs.some((tab) => tab.key === activeWorkTab);
    const currentTab = activeTabStillVisible ? activeWorkTab : tabs[0]?.key || 'upcoming';

    return (
      <div className="sales-table-card dashboard-modern-card dashboard-work-card">
        <div className="dashboard-card-header compact">
          <div>
            <h2>Work Queue</h2>
            <p>Tasks, leads, and opportunities in one compact workspace.</p>
          </div>
        </div>

        <div className="dashboard-tabs compact-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={currentTab === tab.key ? 'active' : ''}
              onClick={() => setActiveWorkTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {currentTab === 'upcoming' && renderTaskTable(tasks, 'No upcoming tasks')}
        {currentTab === 'overdue' && renderTaskTable(overdueTasks, 'No overdue tasks', true)}
        {currentTab === 'leads' && renderRecentLeadsTable()}
        {currentTab === 'opportunities' && renderRecentOpportunitiesTable()}
      </div>
    );
  };

  const settingOptions = [
    { key: 'showPerformance', label: 'Performance' },
    { key: 'showScoreboard', label: 'Scoreboard' },
    { key: 'showAttention', label: 'Attention Needed' },
    { key: 'showPolicyCharts', label: 'Policy & Application Trends' },
    { key: 'showWorkQueue', label: 'Work Queue' },
    { key: 'showRecentLeads', label: 'Recent Leads tab' },
    { key: 'showRecentOpportunities', label: 'Recent Opportunities tab' },
  ];

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page dashboard-page dashboard-modern-page">
        <div className="sales-hero sales-profile-hero dashboard-hero-modern">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">Dashboard</h1>
            <p className="sales-subtitle">
              {summary?.monthLabel
                ? `${summary.monthLabel} production snapshot`
                : 'Live production snapshot'}
            </p>
          </div>

          <button
            type="button"
            className="dashboard-settings-btn"
            onClick={() => setShowDashboardSettings(true)}
            aria-label="Dashboard settings"
            title="Dashboard settings"
          >
            ⚙
          </button>
        </div>

        {renderPolicyCharts()}

        <div className="dashboard-top-grid">
          {renderPerformanceWidget()}
          {renderScoreboardWidget()}
        </div>

        <div className="dashboard-middle-grid dashboard-middle-grid-single">
          {renderAttentionWidget()}
        </div>

        {renderWorkQueueWidget()}
      </div>

      {showDashboardSettings && (
        <div className="dashboard-settings-overlay" onClick={() => setShowDashboardSettings(false)}>
          <div className="dashboard-settings-panel" onClick={(event) => event.stopPropagation()}>
            <div className="dashboard-settings-header">
              <div>
                <p>Dashboard</p>
                <h2>Customize View</h2>
              </div>

              <button type="button" onClick={() => setShowDashboardSettings(false)}>
                ×
              </button>
            </div>

            <div className="dashboard-settings-body">
              {settingOptions.map((option) => (
                <label key={option.key} className="dashboard-setting-row">
                  <span>{option.label}</span>
                  <input
                    type="checkbox"
                    checked={!!dashboardSettings[option.key]}
                    onChange={() => toggleDashboardSetting(option.key)}
                  />
                </label>
              ))}
            </div>

            <div className="dashboard-settings-footer">
              <button type="button" className="sales-secondary-btn" onClick={resetDashboardSettings}>
                Reset
              </button>

              <button
                type="button"
                className="sales-primary-btn"
                onClick={() => setShowDashboardSettings(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {editingKey && (
        <GoalModal
          currentGoal={goals[editingKey]}
          title={`Edit goal for ${metricTitleMap[editingKey] || editingKey}`}
          onSave={(newVal) => {
            const token = localStorage.getItem('token');

            fetch(`${API_BASE_URL}/api/goals/${editingKey}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ goal: newVal }),
            })
              .then((res) => res.json())
              .then((updated) => {
                setGoals((prev) => ({ ...prev, [editingKey]: updated.goal }));
                setEditingKey(null);
                refreshSummary();
              })
              .catch((err) => {
                console.error('❌ Goal update failed:', err);
              });
          }}
          onClose={() => setEditingKey(null)}
        />
      )}

      {showTaskModal && selectedTask && (
        <TaskModal
          task={selectedTask}
          onClose={() => setShowTaskModal(false)}
          onUpdate={() => {
            setShowTaskModal(false);
            refreshTasks();
            refreshSummary();
          }}
          source="dashboard"
        />
      )}
    </>
  );
}

export default Dashboard;